import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { CTA_GHOST, LINK_MUTED } from "@/onboarding/ui/theme";
import { useLocation, useNavigate } from "react-router-dom";
import {
  APPLY_FROM_BUILDER,
  BUILD_ON_BUILDER,
  COMPLETION,
  DEMO_STEPS,
} from "./flows";
import { useDemoGuideStore } from "./useDemoGuideStore";
import { visibleSteps } from "./visibleSteps";
import FlowCompleteCta from "./FlowCompleteCta";

/**
 * The instruction panel for the guided run.
 *
 * Sized to be read rather than tucked away. The first version was a 320px box
 * in the bottom corner of a 2400px screen, which on a monitor that size is
 * indistinguishable from a cookie notice — it was ignored because it looked
 * like something to dismiss. This is roughly twice the width, sets its heading
 * at body size instead of caption size, and carries a squawk-blue edge so it
 * reads as part of the application rather than an overlay on top of it.
 *
 * Bottom-left because Tawk owns bottom-right. It sits over the dashboard,
 * which only works because the layout now packs to the full visible height —
 * before that the canvas ended two-thirds down and this floated in the dead
 * band underneath, pointing at a panel nowhere near it.
 *
 * It never anchors to an element. The dashboard drags, resizes and reflows
 * across five breakpoints, so a measured position would be stale the moment
 * anything moved. The panel it refers to is ringed instead, by grid id.
 */
/** Already on the page a step wants to send them to. */
function alreadyThere(to: string | undefined, pathname: string): boolean {
  if (!to) return false;
  if (to === "/dashboard") {
    return pathname === "/" || pathname.startsWith("/dashboard");
  }
  return pathname.startsWith(to);
}

export default function GuideRail() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { state, exitRun, advanceStep } = useDemoGuideStore((s) => ({
    state: s.state,
    exitRun: s.exitRun,
    advanceStep: s.advanceStep,
  }));

  if (!state?.assetClass) return null;

  const steps = visibleSteps(state.assetClass);
  const done = !!state.completedAt;
  const step = steps[state.step];

  if (!done && !step) return null;

  // The apply step reads differently depending on where they are standing:
  // "apply it to the feed" is not an instruction you can follow on a page with
  // no feed on it.
  const offDashboard =
    step?.id === "highlight" &&
    !(pathname === "/" || pathname.startsWith("/dashboard"));
  // Standing on the builder already: telling them to open it is noise.
  const onBuilder =
    step?.id === "watchlist" && pathname.startsWith("/watchlists-filters");

  const instruction = offDashboard
    ? APPLY_FROM_BUILDER.instruction
    : onBuilder
      ? BUILD_ON_BUILDER.instruction
      : step?.instruction;
  const hint = offDashboard
    ? APPLY_FROM_BUILDER.hint
    : onBuilder
      ? BUILD_ON_BUILDER.hint
      : step?.hint;

  /**
   * The button the rail offers, and what pressing it does.
   *
   * A step with somewhere to be gets taken there — pressing "Open Watchlists"
   * is the same as clicking Watchlists in the sidebar, which is what the
   * instruction was asking for. It does NOT advance the run: arriving on the
   * page is not the same as doing the thing on it, and the step still ends on
   * its own trigger (creating the watchlist, applying it).
   *
   * Steps with nowhere to go and no detectable action keep a plain Next.
   */
  const action = offDashboard
    ? APPLY_FROM_BUILDER.action
    : onBuilder
      ? // Already on the builder: the button stops being "go there" and
        // becomes the thing they were sent to do.
        BUILD_ON_BUILDER.action
      : !alreadyThere(step?.action?.to, pathname)
        ? step?.action
        : undefined;

  /**
   * Which corner keeps out of the way of the thing being asked for.
   *
   * A fixed position cannot work for a guide that points at different parts of
   * different pages: top-right clears the dashboard's leftmost panels but
   * lands squarely on the builder's "Build it for me" card. So it moves — and
   * moving is the lesser evil, because a panel that shifts once between steps
   * is easier to follow than one that covers the button it is naming.
   */
  const onBuilderPage = pathname.startsWith("/watchlists-filters");
  const corner = onBuilderPage
    ? // The builder's two start cards sit top-left and top-centre.
      "bottom-6 right-5"
    : // The dashboard's primary and filtered wires are the leftmost panels.
      "right-5 top-[4.25rem]";

  return (
    /**
     * Top-right on the dashboard, bottom-right on the builder.
     *
     * It used to sit bottom-left, which on a laptop screen covered the panels
     * it was pointing at — the primary and filtered headlines are the two
     * leftmost widgets in every template, so the guide was standing on its own
     * instructions. Everything it asks for happens on the left: the wires, the
     * highlight menu, the popout. The right-hand column holds the smaller
     * supporting feeds, which is the cheapest thing on screen to cover.
     *
     * top-[4.25rem] clears the 40px header and 28px tab bar (see packLayout,
     * which does the same arithmetic for the grid).
     */
    <div
      className={clsx(
        // z-40 puts the rail above the dashboard but BELOW every dialog.
        // At z-[90] it outranked the editors it sends people into (they run
        // z-50 to z-[70]), so on the builder page — where it sits bottom-right
        // to clear the start cards — it landed on top of the combine editor's
        // Save watchlist button and the guide blocked the thing it was asking
        // for. Behind the backdrop is the right place: while a dialog is open,
        // the dialog is the instruction.
        "no-drag fixed z-40 hidden w-[26rem] transition-[top,bottom] duration-300 sm:block lg:w-[30rem] short-screen:w-[24rem]",
        corner
      )}
    >
      {/* Capped against the viewport: the min-height below is a floor, not a
          promise, and on a short screen a 30rem panel would otherwise run off
          the bottom of the window. */}
      {/* Elevated rather than modal, and blue rather than grey.
          On a dark dashboard a gray-900 panel with a gray-700 border is the
          same two colours as every widget behind it, so it read as another
          panel rather than as the app talking. A blue rim alone did not fix
          that — the surface underneath was still the grey every widget uses,
          so the colour read as trim on a panel rather than as the panel's own.
          The wash carries the blue across the whole surface; the rim and ring
          deepen to match. Depth (drop shadow, blurred backdrop) still does the
          separating, so the dashboard stays visible and usable underneath,
          which a scrim would have cost. */}
      <div className="flex max-h-[calc(100vh-5.5rem)] flex-col overflow-hidden rounded-xl border border-squawk-blue/70 bg-gradient-to-b from-squawk-blue/[0.12] to-gray-900/95 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.9)] ring-1 ring-squawk-blue/30 backdrop-blur-md">
        {/* Accent edge — the panel has to read as the app talking, not as an
            ad sitting on top of it. */}
        <div className="h-1.5 w-full bg-squawk-blue" />

        <div className="flex min-h-[22rem] flex-col overflow-y-auto p-6 scrollbar-thin lg:min-h-[26rem] lg:p-7 short-screen:min-h-0 short-screen:p-5">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-squawk-blue">
                Guided demo
              </p>
              {!done && (
                <p className="mt-1 text-sm text-gray-500">
                  Step {Math.min(state.step + 1, steps.length)} of{" "}
                  {steps.length}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={exitRun}
              aria-label="Close guide"
              data-analytics-id="demo-guide-close"
              className="-m-1 rounded p-1 text-gray-500 transition-colors hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-6 flex items-center gap-1.5 short-screen:mb-4" aria-hidden>
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={clsx(
                  "h-1 flex-1 rounded-full transition-colors duration-500",
                  // Reached, including the one in play, reads as solid blue;
                  // what is left reads as grey. Finished steps used to render
                  // at 40% against a solid current step, which made the work
                  // already done look fainter than the work in hand — a
                  // progress bar that dims behind you.
                  done || i <= state.step ? "bg-squawk-blue" : "bg-gray-800"
                )}
              />
            ))}
          </div>

          <div
            key={done ? "done" : step!.id}
            className="nq-onb-rise flex flex-1 flex-col"
          >
            {done ? (
              <FlowCompleteCta completion={COMPLETION} />
            ) : (
              <>
                <p className="text-2xl font-semibold leading-snug text-gray-100 short-screen:text-xl">
                  {instruction}
                </p>
                {hint && (
                  <p className="mt-3 text-base leading-relaxed text-gray-300 short-screen:mt-2 short-screen:text-sm">
                    {hint}
                  </p>
                )}
                {action ? (
                  <button
                    type="button"
                    onClick={() => navigate(action.to)}
                    className={`${CTA_GHOST} mt-auto w-full py-3.5 text-base`}
                    // Navigation, not completion — this button does not fire
                    // demo_step, because arriving on a page is not doing the
                    // thing on it. The id is here so the distinction stays
                    // legible in a click-selector report.
                    data-analytics-id={`demo-guide-goto-${step!.id}`}
                  >
                    {action.label}
                  </button>
                ) : (
                  step!.trigger.kind === "manual" && (
                    <button
                      type="button"
                      onClick={() => advanceStep()}
                      className={`${CTA_GHOST} mt-auto w-full py-3.5 text-base`}
                      data-analytics-id={`demo-guide-next-${step!.id}`}
                    >
                      Next
                    </button>
                  )
                )}
              </>
            )}
          </div>

          {/* Always present. The primary button navigates rather than
              advancing, so without this a manual step could have no way
              forward at all — which is how someone gets stuck on the page the
              guide just sent them to. */}
          {!done && (
            <div className="mt-6 flex items-center justify-between gap-3 border-t border-gray-800 pt-4">
              {step!.trigger.kind !== "manual" ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                  <CheckIcon className="h-3.5 w-3.5" />
                  Continues when you do it
                </span>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={() => advanceStep({ skipped: true })}
                className={LINK_MUTED}
                data-analytics-id={`demo-guide-skip-${step!.id}`}
              >
                Skip
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { DEMO_STEPS };
